'use client';

import { useEffect, useMemo, useRef } from 'react';
import { usePathname, useSearchParams } from 'next/navigation';
import { useCompanyPreview } from '@/new_components/searchresults/useCompanyPreview';
import { searchParamsWithoutPreview } from '@/lib/search-filters';
import { useAiSearchProgress } from '@/lib/ai-search-progress';
import type { FilterParams } from '@/lib/search-filter-params';
import { useTranslation } from 'react-i18next';
import { buildInitialFilters } from './searchPageFilters';
import type { SearchPageClientProps } from './searchPageTypes';
import { SearchResultsMain } from './SearchResultsMain';
import { SearchPageSidebar } from './SearchPageSidebar';
import { useAiSearchOrigin, useCityList, useDesktopBodyLock, useFilterCountBroadcast, useFilterLists, useFiltersPanel, useSearchViewer } from './useSearchPageChrome';
import { useExecuteSearch, useSearchResults } from './useSearchResults';
import { usePagingHandlers, useResultsSideActions, useSavedSearchActions, useSearchSubmit } from './useSearchActions';

// The page's state lives in hooks, called in the order their effects must run
// (useSearchPageChrome.ts, then useSearchResults.ts, then useSearchActions.ts).
// A new filter needs nothing here: its state is staged in the sidebar
// (useSidebarFilterState.ts) and travels in FilterParams.

export default function SearchPageClient({
  naceCodes: initialNaceCodes,
  cityList: initialCityList,
  tagList: initialTagList,
  searchResponse: initialSearchResponse,
  searchSkipped,
  initialOffset,
  initialSort,
  initialLimit,
  initialFinancialYear,
  initialTier,
  presetFilters,
  listSlug,
  resultsHeader,
}: SearchPageClientProps) {
  const { t } = useTranslation();
  const urlSearchParams = useSearchParams();
  const renderedSearchParams = searchParamsWithoutPreview(urlSearchParams).toString();
  const searchParams = useMemo(
    () => new URLSearchParams(renderedSearchParams),
    [renderedSearchParams],
  );
  const { previewEuId, openPreview, closePreview } = useCompanyPreview();
  const pathname = usePathname();
  const aiProgress = useAiSearchProgress();
  const aiIsLoading = !listSlug && (aiProgress.phase === 'interpreting' || aiProgress.phase === 'loading');
  const aiOriginRef = useAiSearchOrigin(renderedSearchParams);
  const { locale, session, sessionStatus, recordRecentSearch, isProOrAdmin, tier } =
    useSearchViewer(initialTier);
  const { naceCodes, tagList } = useFilterLists(initialNaceCodes, initialCityList, initialTagList);
  const { filtersOpen, setFiltersOpen } = useFiltersPanel();

  // Mobile shows results as cards capped at 10 per page. If the initial limit
  // (URL or default) is larger, re-issue the search at 10 once on mount so the
  // pagination steps line up with the visible cards.
  const mobileLimitInitRef = useRef(false);
  useEffect(() => {
    if (mobileLimitInitRef.current) return;
    if (window.innerWidth >= 1024) return;
    mobileLimitInitRef.current = true;
    if (results.currentLimit > 10) {
      paging.handleLimitChange(10);
    }
    // handleLimitChange is stable per the deps below; we intentionally only
    // want this to run once on mount.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);
  useDesktopBodyLock();

  const initialFilters: FilterParams = useMemo(
    () => buildInitialFilters(searchParams, presetFilters, listSlug, pathname, locale),
    [searchParams, presetFilters, listSlug, pathname, locale],
  );
  useFilterCountBroadcast(initialFilters, tier);
  const cityList = useCityList(initialFilters.country, initialCityList);

  const results = useSearchResults({
    initialSearchResponse, renderedSearchParams, searchParams, searchSkipped, initialOffset,
    initialSort, initialLimit, initialFinancialYear, initialFilters, tier, sessionStatus,
    recordRecentSearch, aiOriginRef,
  });
  const executeSearch = useExecuteSearch(results, naceCodes, listSlug, locale);
  const { applyCountry, handleSubmit } = useSearchSubmit(results, executeSearch, searchParams, setFiltersOpen);
  const saved = useSavedSearchActions(results, executeSearch, handleSubmit, initialFilters, naceCodes);
  const paging = usePagingHandlers(results, executeSearch, initialFilters);
  const sideActions = useResultsSideActions(
    results, naceCodes, renderedSearchParams, searchParams, isProOrAdmin, initialFilters,
  );

  return (
    <div className="search-warm-bg relative lg:fixed lg:left-[var(--app-sidebar-width,0px)] lg:right-0 lg:w-[calc(100%-var(--app-sidebar-width,0px))] lg:top-[var(--header-height,95px)] lg:bottom-[calc(var(--footer-height,60px)+env(safe-area-inset-bottom,0px))] flex flex-col lg:flex-row min-h-0 w-full max-w-full lg:overflow-hidden bg-background">
      {/* The sr-only H1 labels the page for assistive tech on /search. When a
        * `resultsHeader` is provided (e.g. /listen/[slug] passes a visible H1
        * in its hero), we skip it to keep a single H1 per page for SEO. */}
      {resultsHeader == null && (
        <h1 className="sr-only">{t('search.sidebar.title')}</h1>
      )}

      <SearchPageSidebar
        naceCodes={naceCodes} cityList={cityList} tagList={tagList} initialFilters={initialFilters}
        tier={tier} session={session} filtersOpen={filtersOpen} setFiltersOpen={setFiltersOpen}
        results={results} paging={paging} saved={saved} handleSubmit={handleSubmit}
        applyCountry={applyCountry}
      />

      <SearchResultsMain
        t={t}
        resultsHeader={resultsHeader}
        aiIsLoading={aiIsLoading}
        aiProgress={aiProgress}
        listSlug={listSlug}
        initialFilters={initialFilters}
        tier={tier}
        handleSubmit={handleSubmit}
        previewEuId={previewEuId}
        openPreview={openPreview}
        results={results}
        paging={paging}
        sideActions={sideActions}
        isProOrAdmin={isProOrAdmin}
        session={session}
        closePreview={closePreview}
      />
    </div>
  );
}
